/**
 * Groups a review's findings for the collective review list (AIVA-8751):
 * category → rule → findings, with the counts the list shows. Pure, so the
 * grouping and counting rules are testable without rendering anything.
 */
import { GROUPS, isJudgedRule, ruleGroup, ruleName, ruleOrder, type RuleGroupId } from "./ruleCatalog";
import type { Finding } from "./types";

/** The operator has made a decision about it. */
export function isDecided(finding: Finding): boolean {
  return finding.status === "applied" || finding.status === "verified" || finding.status === "skipped";
}

export function isApplied(finding: Finding): boolean {
  return finding.status === "applied" || finding.status === "verified";
}

/** Still awaiting a decision and appliable: pending or anchored. A stale
 *  finding is still undecided, but Apply is refused against it. */
export function isOpen(finding: Finding): boolean {
  return finding.status === "pending" || finding.status === "anchored";
}

export interface RuleGroup {
  readonly ruleId: string;
  readonly name: string;
  readonly judged: boolean;
  /** Every finding of this rule, in the order the server returned them. */
  readonly findings: readonly Finding[];
  /** The ones still needing a decision — what the rule's pager walks. Open
   *  findings first, then stale ones, so an appliable one is always shown
   *  before one that can only be skipped. */
  readonly undecided: readonly Finding[];
}

export interface CategoryGroup {
  readonly id: RuleGroupId;
  readonly label: string;
  readonly rules: readonly RuleGroup[];
  readonly undecidedCount: number;
}

export function groupFindings(findings: readonly Finding[]): CategoryGroup[] {
  const byRule = new Map<string, Finding[]>();
  for (const finding of findings) {
    const list = byRule.get(finding.rule_id);
    if (list) list.push(finding);
    else byRule.set(finding.rule_id, [finding]);
  }

  const rules: RuleGroup[] = [...byRule.entries()]
    .sort(([a], [b]) => ruleOrder(a) - ruleOrder(b) || a.localeCompare(b))
    .map(([ruleId, list]) => ({
      ruleId,
      name: ruleName(ruleId),
      judged: isJudgedRule(ruleId),
      findings: list,
      undecided: [...list.filter(isOpen), ...list.filter((f) => f.status === "stale")],
    }));

  return GROUPS.map(({ id, label }) => {
    const inCategory = rules.filter((rule) => ruleGroup(rule.ruleId) === id);
    return {
      id,
      label,
      rules: inCategory,
      undecidedCount: inCategory.reduce((n, rule) => n + rule.undecided.length, 0),
    };
  }).filter((category) => category.rules.length > 0);
}

export interface ReviewTotals {
  readonly total: number;
  readonly decided: number;
  readonly applied: number;
  readonly skipped: number;
  /** Undecided findings Apply was refused for — the document changed. */
  readonly stale: number;
  /** Open findings with no marker in the document — see `isUnmarked`. */
  readonly unmarked: number;
}

export function reviewTotals(findings: readonly Finding[]): ReviewTotals {
  return {
    total: findings.length,
    decided: findings.filter(isDecided).length,
    applied: findings.filter(isApplied).length,
    skipped: findings.filter((f) => f.status === "skipped").length,
    stale: findings.filter((f) => f.status === "stale").length,
    unmarked: findings.filter(isUnmarked).length,
  };
}

/** AIVA-9179: still open, but the anchor step never placed its marker — it
 *  timed out, or Word could not search the text. Jump and Apply still work,
 *  by finding the text again (AIVA-8209); the panel says so rather than
 *  presenting the card as marked. */
export function isUnmarked(finding: Finding): boolean {
  return isOpen(finding) && !finding.anchor_tag;
}

/** Where the list should move once the open rule has nothing left to decide:
 *  the next rule with undecided findings in the same group, else the first
 *  such rule in a later group, wrapping round — or null when everything is
 *  decided. */
export function nextUndecided(
  groups: readonly CategoryGroup[],
  from: { categoryId: RuleGroupId; ruleId: string },
): { categoryId: RuleGroupId; ruleId: string } | null {
  const flat = groups.flatMap((g) => g.rules.map((r) => ({ categoryId: g.id, rule: r })));
  const start = flat.findIndex((x) => x.rule.ruleId === from.ruleId);
  for (let step = 1; step <= flat.length; step++) {
    const candidate = flat[(Math.max(start, 0) + step) % flat.length];
    if (candidate && candidate.rule.undecided.length > 0) {
      return { categoryId: candidate.categoryId, ruleId: candidate.rule.ruleId };
    }
  }
  return null;
}

/** The first rule with anything undecided, for the initial open state. */
export function firstUndecided(groups: readonly CategoryGroup[]): { categoryId: RuleGroupId; ruleId: string } | null {
  for (const group of groups) {
    const rule = group.rules.find((r) => r.undecided.length > 0);
    if (rule) return { categoryId: group.id, ruleId: rule.ruleId };
  }
  return null;
}
